import { writeIdleMarks, type SettingsStore } from "./settings.ts";

/**
 * Where the nameplates read the Show idle and AFK marks choice each frame: the game report's nameplates entry.
 */
export interface IdleMarksTarget {
  idleMarks: boolean;
}

/**
 * The page's Show idle and AFK marks choice (P1.9-T45, ADR-090 decision 9), as `HealthNumbersSetting` holds Show
 * health numbers: made from what the store holds as the page loads, put on the target the nameplates read every
 * frame, and taking each change Settings makes, which applies on the next frame and is kept per viewer. It changes only
 * what this viewer draws; nothing is sent.
 *
 * @example
 * ```ts
 * const idleMarks = new IdleMarksSetting(gameReport.nameplates, readIdleMarks());
 * idleMarks.setShow(false);
 * ```
 */
export class IdleMarksSetting {
  /**
   * @param target Where the nameplates read the choice.
   * @param show Whether the marks show as the page loads, as `readIdleMarks` gives it.
   * @param store Where a change is kept; this browser's `localStorage` when omitted.
   */
  constructor(
    private readonly target: IdleMarksTarget,
    show: boolean,
    private readonly store?: SettingsStore | null,
  ) {
    target.idleMarks = show;
  }

  /**
   * Whether the marks show now.
   *
   * @returns The choice.
   */
  get show(): boolean {
    return this.target.idleMarks;
  }

  /**
   * Takes the viewer's choice, which applies on the next frame, and keeps it.
   *
   * @param show Whether the marks show.
   */
  setShow(show: boolean): void {
    this.target.idleMarks = show;
    writeIdleMarks(show, this.store);
  }
}
